import React, { FC, lazy, Suspense, useEffect, useState } from 'react';
import timeLimit from 'time-limit-promise';
import captureException from './utils/sentry';
import { Boundary } from './components/ErrorBoundary';
import { logError, logInfo } from './shared/utils/logging';
import {
  createDraftOrderForCandyCart,
  getOffers,
  trackAddToCart,
} from './api/candycart';
import { getCurrentLocale } from './shared/api/utils';
import {
  addToCart,
  getCart,
  removeFromCart,
} from './shared/api/shared-app-requests';
import { ShopifyAddToCartResponse } from './shared/api/types';
import { shouldTrackAddToCart } from './shared/api/utils';
import {
  cleanupInvalidDiscountData,
  removeCandyRackCheckoutButtonDiscount,
  saveDiscountForCandyRackCheckoutButton,
} from './shared/utils/discounts';
import {
  ProductsContextConsumer,
  ProductsContextProvider,
} from './contexts/ProductsContext';
import {
  getAddedOffers,
  removeOffer,
  saveAddedOffer,
} from './utils/candycart/products';
import { getAttributes } from './utils/candycart/attributes';
import {
  CandyCartSettings,
  CandyCartText,
  CheckoutButtonClickedCustomEventDetail,
  Offer,
  OffersResponseBody,
  Product,
} from './types';
import {
  CandyCartContextConsumer,
  CandyCartContextProvider,
} from './contexts/CandyCartContext';
import {
  CandyRackContextConsumer,
  CandyRackContextProvider,
} from './contexts/CandyRackContext';
import { validateFormForCandyCart } from './utils/candycart/validation';
import { REGISTERED_APPS } from './shared/types/Apps';
import { SharedContextProvider } from './contexts/SharedContext';
import { initializeIntelligemsTestFacade } from './shared/test-facade/intelligemsTestFacade';
import reportChunkError from './dissolve/services/chunkErrorMonitoring';
import {
  APIError,
  APPError,
  isAPIError,
  isNetworkError,
  NetworkError,
} from './shared/api/types';
import { generateDiscountCode } from './shared/api/candyrack';
import { appFunctions } from './utils/app-utils';
import Buttons from './dom/CandyCart/buttons';

import { CHECKOUT_BUTTON_TRANSLATIONS_KEY } from './graphql/constants';

import getTranslationsMetaobject from './graphql/utils/getTranslationsMetaobject';
import { getTranslatedOffersForCandyRack } from './graphql/utils/getTranslatedOffers';
import { AppInfoCandyRack } from './shared/types/AppsInfo';
import { AppsInfoProvider } from './contexts/AppsInfoContext';

import { applyIntelligemsFilterForCheckoutButton } from './shared/product-page-placement/intelligemsOfferFilterUtils';
import { logIntelligemsStatus } from './shared/product-page-placement/intelligemsUtils';
import { isHorizonTheme } from './shared/utils/theme-detection';
import { setMoneyFormats } from './utils/popup-utils';

interface Props {
  noProductOffersAvailable?: boolean;
  buttons: Buttons;
  appsInfo: AppInfoCandyRack;
}

interface AppProps extends Props {
  setCartTotal: (total: number) => void;
  removeOffersWithUnavailableProducts: (offers: Offer[]) => Promise<Offer[]>;
  removeOffersWithProductAlreadyInCart: (offers: Offer[]) => Promise<Offer[]>;
}

const Popup = lazy(() =>
  import(/* webpackPrefetch: true */ './components/Popup').catch(() =>
    reportChunkError(REGISTERED_APPS.CHECKOUT_BUTTON, window.Shopify.shop),
  ),
);

const App: FC<AppProps> = ({
  setCartTotal,
  removeOffersWithUnavailableProducts,
  removeOffersWithProductAlreadyInCart,
  noProductOffersAvailable,
  buttons,
  appsInfo,
}) => {
  const [button, setButton] = useState<HTMLInputElement>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [cart, setCart] = useState<any>();
  const [settings, setSettings] = useState<CandyCartSettings>();
  const [error, setError] = useState<APPError>();
  const [showError, setShowError] = useState(false);

  const handleExternalCheckoutButtonClick = async ({
    detail: { clonedButton, hiddenButton },
  }: CustomEvent<CheckoutButtonClickedCustomEventDetail>) => {
    window.CandyCart.buttonState = 'handling';

    if (
      // on network error the popup is set to open
      window.CandyCart?.popupOpen ||
      !validateFormForCandyCart(clonedButton)
    ) {
      hiddenButton.click();
      return;
    }

    /*
     ** Doubled time limit to 8000, as we potentially ask CR and CC for offers,
     ** which can be heavy operations, as we do requests to Shopify
     */
    const offers = await timeLimit(getOffersLocal(), 8000);

    if (!offers || offers.length === 0) {
      hiddenButton.click();
      return;
    }

    // Horizon theme specific logic - close cart drawer before opening modal
    if (isHorizonTheme()) {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof (cartDrawer as any).close === 'function') {
        (cartDrawer as any).close();
      }
    }

    setModalOpen(true);
    setButton(hiddenButton);
  };

  useEffect(() => {
    document.addEventListener(
      'checkoutButtonClicked',
      handleExternalCheckoutButtonClick as unknown as EventListener,
    );
    return () => {
      document.removeEventListener(
        'checkoutButtonClicked',
        handleExternalCheckoutButtonClick as unknown as EventListener,
      );
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      noProductOffersAvailable &&
      modalOpen &&
      offers &&
      offers.every(
        (offer: Offer) =>
          offer.is_product && offer?.type !== 'smart_upsell_offer',
      )
    ) {
      setModalOpen(false);
      clickButton();
    }
    // eslint-disable-next-line
  }, [noProductOffersAvailable, modalOpen, offers]);

  useEffect(() => {
    // Initialize test facade for QA Robot Framework integration tests
    initializeIntelligemsTestFacade(REGISTERED_APPS.CHECKOUT_BUTTON);
    // Check Intelligems status
    logIntelligemsStatus(
      appsInfo.is_intelligems_enabled,
      REGISTERED_APPS.CHECKOUT_BUTTON,
    );
    logInfo('mounted 🎉', REGISTERED_APPS.CHECKOUT_BUTTON);
    window.CandyCart.isLoading = false;
    // eslint-disable-next-line
  }, []);

  const updateSettings = (offersResponse: OffersResponseBody) => {
    const settings = offersResponse.settings as CandyCartSettings;
    setSettings(settings);
  };

  const getOffersLocal = async () => {
    try {
      const cart = await getCart();
      const offersResponseFromBE = await getOffers(cart);
      if (!offersResponseFromBE) {
        return null;
      }
      const filteredOffersResponse: OffersResponseBody = {
        ...offersResponseFromBE,
        offers: applyIntelligemsFilterForCheckoutButton(
          offersResponseFromBE.offers,
          appsInfo.is_intelligems_enabled,
        ),
      };
      // Store money formats for CandyCart so currency.ts can use the store template
      setMoneyFormats(
        filteredOffersResponse.money_formats,
        filteredOffersResponse.currency !== window.Shopify?.currency?.active,
        REGISTERED_APPS.CHECKOUT_BUTTON,
      );

      if (!window.CANDYCART_AJAX_CART_WATCHER_SELECTOR) {
        // functionality only relevant to cloned buttons
        buttons.initCustomCartWatcherForCloneButtons(
          (filteredOffersResponse.settings as CandyCartSettings)
            .ajax_cart_watcher_selector,
        );
      }

      const translationsMetaobjectData = await getTranslationsMetaobject(
        appsInfo.storefront_access_token,
        CHECKOUT_BUTTON_TRANSLATIONS_KEY,
      );

      // If translations are fetched, we use them, otherwise we default to fallback
      if (translationsMetaobjectData) {
        const { add, added, back, subtitle, continue_to, title, headline } =
          translationsMetaobjectData as unknown as CandyCartText;

        // Setting translated strings, in case of empty string, we also display fallback
        const settingsWithUpdatedTranslations: CandyCartSettings = {
          ...(filteredOffersResponse.settings as CandyCartSettings),
          text: {
            add: add || filteredOffersResponse.settings.text.add,
            added: added || filteredOffersResponse.settings.text.added,
            back: back || filteredOffersResponse.settings.text.back,
            subtitle: subtitle || filteredOffersResponse.settings.text.subtitle,
            continue_to:
              continue_to ||
              (filteredOffersResponse.settings as CandyCartSettings).text
                .continue_to,
            title:
              title ||
              (filteredOffersResponse.settings as CandyCartSettings).text.title,
            headline:
              headline ||
              (filteredOffersResponse.settings as CandyCartSettings).text
                .headline,
          },
        };

        updateSettings({
          ...filteredOffersResponse,
          settings: settingsWithUpdatedTranslations,
        });
      } else {
        updateSettings(filteredOffersResponse);
      }

      // Translating offer strings - returns offers in case of no translations
      const offersTranslated = await getTranslatedOffersForCandyRack(
        filteredOffersResponse.offers,
        appsInfo.storefront_access_token,
      );

      // First filter out unavailable products, then filter out products already in cart
      const offersWithoutUnavailableProducts =
        await removeOffersWithUnavailableProducts(offersTranslated);
      const finalFilteredOffers = await removeOffersWithProductAlreadyInCart(
        offersWithoutUnavailableProducts,
      );

      setOffers(finalFilteredOffers);
      setCart(cart);
      setCartTotal(cart.total_price / 100);
      return finalFilteredOffers;
    } catch (error) {
      logError(
        'Error while getting offers',
        REGISTERED_APPS.CHECKOUT_BUTTON,
        error,
      );
      captureException(error as Error);
    }
  };

  const clickButton = () => {
    button?.click();
  };

  const handleClose = async () => {
    setModalOpen(false);
    clickButton();
  };

  const handleContinue = async () => {
    if (error && showError) {
      clickButton();
      return;
    }
    const offers = getAddedOffers();
    if (offers.length === 0 || appFunctions.isBaseApp()) {
      logInfo(
        'No offers added via CC draft orders using normal checkout...',
        REGISTERED_APPS.CHECKOUT_BUTTON,
      );

      if (appsInfo.is_discount_message_enabled) {
        const cart = await getCart();
        await cleanupInvalidDiscountData(cart, REGISTERED_APPS.ADD_TO_CART);
      }

      clickButton();
      return;
    }
    try {
      cart.attributes = {
        ...cart.attributes,
        ...getAttributes(),
      };
      const noteElement =
        document.querySelector<HTMLInputElement>('[name=note]');
      if (noteElement) {
        cart.note = noteElement.value;
      }
      const res = await createDraftOrderForCandyCart(cart, offers);
      const redirectUrl = getRedirectUrl(res);

      if (redirectUrl) {
        const shopLocale = getCurrentLocale(window.location.pathname);
        const localizedRedirectUrl =
          redirectUrl + (shopLocale ? `?locale=${shopLocale}` : '');
        logInfo(
          `Continue: redirecting to ${localizedRedirectUrl}`,
          REGISTERED_APPS.CHECKOUT_BUTTON,
        );
        window.location.href = localizedRedirectUrl;
        setModalOpen(false);
        if (button) {
          const buttonRef = button;
          (buttonRef as HTMLInputElement).disabled = true;
          setButton(buttonRef);
        }
      } else {
        handleError();
      }
    } catch (error) {
      logError(
        'Draft order creation failed',
        REGISTERED_APPS.CHECKOUT_BUTTON,
        error,
      );
      handleError(error as APPError);
    }
  };

  const handleNetworkError = () => {
    window.CandyCart.popupOpen = true;
  };

  const getRedirectUrl = (res: any): string | undefined => {
    if (isAPIError(res)) {
      logError(res as APIError, REGISTERED_APPS.CHECKOUT_BUTTON);
      captureException({
        name: 'APIError: creating draft order failed',
        message: (res as APIError).error_code,
      });
    } else if (isNetworkError(res)) {
      logError(res as NetworkError, REGISTERED_APPS.CHECKOUT_BUTTON);
    } else {
      return res.redirect_url;
    }
  };

  const handleError = (appError?: any) => {
    if (error) {
      setShowError(true);
    } else {
      const customError = {
        ...appError,
        message:
          "We couldn't add the items to the order. Please contact us for more details.",
      } as APPError;
      setError(customError);
    }
  };

  // retry request again in 2 seconds
  useEffect(() => {
    if (!error || (error && showError)) return;
    const timeout = setTimeout(handleContinue, 2000);
    return () => {
      clearInterval(timeout);
    };
    // eslint-disable-next-line
  }, [error]);

  const handleAddToCart = async (offer: Offer, product?: Product) => {
    let response: ShopifyAddToCartResponse = null;
    if (appFunctions.isBaseApp()) {
      const properties = offer.noteComputedInFE
        ? { note: offer.noteComputedInFE }
        : undefined;
      response = await addToCart(
        offer.shopifyProductVariantIdComputedInFE,
        offer.quantityComputedInFE,
        properties,
      );

      if (response?.status === 422) {
        // TODO: APPS-10087 - We shouldn't depend on hardcoded English messages.
        // This string matching is fragile and will break with translations or message format changes.
        // This will be addressed in https://digismoothie.atlassian.net/browse/APPS-10087
        const [, itemsActuallyAdded] =
          (typeof response.message === 'string'
            ? response.message.match(
                /Only (\d+) items were added to your cart due to availability./,
              )
            : null) ?? [];

        if (itemsActuallyAdded) {
          // Product(s) has been added but not all of them. Update the quantity to the actual number of items added.
          offer.quantityComputedInFE = parseInt(itemsActuallyAdded, 10);
        } else {
          logInfo(response.description, REGISTERED_APPS.CHECKOUT_BUTTON);
          return false;
        }
      }

      if (offer.apply_discount) {
        logInfo(
          `Applying offer discount code: ${offer.discount_code}`,
          REGISTERED_APPS.CHECKOUT_BUTTON,
        );
        let discountCode = offer.discount_code;
        if (
          (offer.use_recommended_products || offer.is_same_product_offer) &&
          !discountCode &&
          // Discounts apply only to non-service products.
          // The path is followed only for non-service products, which ensures the product exists.
          // Explicitly checking for product as a defensive programming measure.
          product
        ) {
          discountCode = await generateDiscountCode(
            window.Shopify.shop,
            offer.id,
            product.id,
            offer.quantityComputedInFE || 1,
          );
          logInfo(
            `Generated discount code instead: ${discountCode}`,
            REGISTERED_APPS.CHECKOUT_BUTTON,
          );
        }
        await saveDiscountForCandyRackCheckoutButton(
          discountCode,
          offer.shopifyProductVariantIdComputedInFE,
          appsInfo.storefront_access_token,
        );
      }
    } else {
      saveAddedOffer(offer);
    }
    try {
      // Only track if add to cart was completely successful (no status field = success)
      const addedSuccessfully = shouldTrackAddToCart(response);
      if (addedSuccessfully) {
        await trackAddToCart(offer, cart, product?.id);
      }
      logInfo(
        'dispatching candycart-offer-added',
        REGISTERED_APPS.CHECKOUT_BUTTON,
      );
      document.dispatchEvent(
        new CustomEvent('candycart-offer-added', {
          detail: { offer },
        }),
      );
    } catch (error) {
      captureException(error as Error);
    }
    return true;
  };

  const handleRemoveFromCart = async (offer: Offer, variantId?: number) => {
    try {
      if (appFunctions.isBaseApp()) {
        if (offer.apply_discount) {
          removeCandyRackCheckoutButtonDiscount(
            offer.discount_code,
            offer.shopifyProductVariantIdComputedInFE,
          );
        }
        if (variantId) {
          if (offer.is_same_product_offer) {
            await removeFromCart(variantId, 1);
          } else {
            await removeFromCart(variantId);
          }
        }
      } else {
        removeOffer(offer, variantId);
      }
      document.dispatchEvent(
        new CustomEvent('candycart-offer-removed', {
          detail: { offer },
        }),
      );
      logInfo(
        'dispatching candycart-offer-removed',
        REGISTERED_APPS.CHECKOUT_BUTTON,
      );
    } catch (error) {
      captureException(error as Error);
    }
  };

  return (
    <Boundary onError={handleNetworkError}>
      <Suspense fallback={<div />}>
        {settings ? (
          <Popup
            offers={offers}
            settings={settings}
            modalOpen={modalOpen}
            handleAddToCart={handleAddToCart}
            handleRemoveFromCart={handleRemoveFromCart}
            handleContinue={handleContinue}
            handleClose={handleClose}
            currentApp={REGISTERED_APPS.CHECKOUT_BUTTON}
            error={showError ? error : undefined}
            onError={clickButton}
          />
        ) : (
          <></>
        )}
      </Suspense>
    </Boundary>
  );
};

const CandyCartApp = (props: Props) => (
  <SharedContextProvider
    currentAppName={REGISTERED_APPS.CHECKOUT_BUTTON}
    storefrontAccessToken={props.appsInfo.storefront_access_token}
    useDSRecommEngine={props.appsInfo.use_ds_recomm_engine ?? false}
  >
    <AppsInfoProvider appsInfo={props.appsInfo}>
      <ProductsContextProvider
        storefrontAccessToken={props.appsInfo.storefront_access_token}
      >
        <ProductsContextConsumer>
          {({
            noProductOffersAvailable,
            removeOffersWithUnavailableProducts,
          }) => (
            <CandyRackContextProvider
              storefrontAccessToken={props.appsInfo.storefront_access_token}
              useDSRecommEngine={props.appsInfo.use_ds_recomm_engine ?? false}
            >
              <CandyRackContextConsumer>
                {({ removeOffersWithProductAlreadyInCart }) => (
                  <CandyCartContextProvider>
                    <CandyCartContextConsumer>
                      {({ setCartTotal }) => (
                        <App
                          {...props}
                          setCartTotal={setCartTotal}
                          noProductOffersAvailable={noProductOffersAvailable}
                          removeOffersWithUnavailableProducts={
                            removeOffersWithUnavailableProducts
                          }
                          removeOffersWithProductAlreadyInCart={
                            removeOffersWithProductAlreadyInCart
                          }
                        />
                      )}
                    </CandyCartContextConsumer>
                  </CandyCartContextProvider>
                )}
              </CandyRackContextConsumer>
            </CandyRackContextProvider>
          )}
        </ProductsContextConsumer>
      </ProductsContextProvider>
    </AppsInfoProvider>
  </SharedContextProvider>
);

export default CandyCartApp;
